import { useTranslation } from 'react-i18next';
import { Card, CardTitle } from '../ui/Card.js';
import { Icon } from '../Icon.js';
import { Dropdown } from '../ui/Dropdown.js';
import { Pill, type PillTone } from '../PillGroup.js';
import { DocKV } from './EstimateDetail.js';
import { formatMoney } from '../../lib/money.js';
import { paymentMethodLabel } from '../../lib/paymentMethodLabel.js';
import type { Invoice } from '../../hooks/useBilling.js';
import {
  usePaymentCapability, useRecurringCharges, useInvoicePayments,
  useSetInvoicePaymentMode, type RecurringCharge,
} from '../../hooks/useClientPayments.js';

// ── Tarjeta "Cobro" del detalle de factura (Fase 4, 2026-08-31) ──────────────
// docs/PLAN_cobro_a_clientes.md
//
// Contesta las dos preguntas que Eduardo hace mirando una factura: en qué está
// el cobro, y —si es recurrente— cuándo es el próximo y si la tarjeta sigue
// viva.
//
// No se renderiza en una cuenta sin pasarela: ahí la factura se marca a mano y
// esta tarjeta no tendría nada que decir. Es el mismo criterio que
// getGatewayForAccount devolviendo null — modo manual es un estado válido, no
// una carencia que haya que señalar en pantalla.

const PAYMENT_STATUS_TONES: Record<Invoice['paymentStatus'], PillTone> = {
  none: 'neutral',
  awaiting: 'cyan',
  paid: 'success',
  failed: 'danger',
};

const CHARGE_STATUS_TONES: Record<RecurringCharge['status'], PillTone> = {
  pending_card: 'amber',
  active: 'success',
  past_due: 'danger',
  canceled: 'neutral',
};

function formatFecha(ms: number): string {
  return new Date(ms).toLocaleDateString('es', { year: 'numeric', month: 'short', day: 'numeric' });
}

export interface InvoicePaymentCardProps {
  invoice: Invoice;
}

export function InvoicePaymentCard({ invoice }: InvoicePaymentCardProps) {
  const { t } = useTranslation();
  const { data: capability } = usePaymentCapability();
  const { data: chargesData } = useRecurringCharges();
  const { data: paymentsData } = useInvoicePayments(invoice.id);
  const setMode = useSetInvoicePaymentMode();

  if (!capability?.canCharge) return null;

  const charge = invoice.recurringChargeId
    ? chargesData?.charges.find(c => c.id === invoice.recurringChargeId) ?? null
    : null;
  const payments = paymentsData?.payments ?? [];

  // El modo se elige mientras la factura no se haya cobrado. Una vez cobrada,
  // cambiarlo sería reescribir cómo se cobró algo que ya se cobró — el servidor
  // lo rechaza igual (409), esto solo evita ofrecerlo.
  const editable = invoice.paymentStatus !== 'paid'
    && invoice.status !== 'paid'
    && invoice.status !== 'void';

  // Por qué el cliente todavía no puede pagar en línea. El server lo calcula
  // (invoicePayBlockers) y acá se dice: antes el documento mostraba un botón
  // de pagar que no llevaba a ningún lado, y desde esta pantalla no había
  // forma de saber qué faltaba.
  const bloqueos = invoice.payBlockers ?? [];
  const avisoPago = bloqueos.length > 0 && bloqueos[0] !== 'paid'
    ? t(`billing.payment.blocker.${bloqueos[0]}`, { defaultValue: '' })
    : '';

  return (
    <Card header={<CardTitle>{t('billing.payment.title')}</CardTitle>}>
      {avisoPago && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 'var(--evo-space-2)',
          padding: 'var(--evo-space-3)', marginBottom: 'var(--evo-space-3)',
          border: '1px solid var(--evo-amber)', borderRadius: 'var(--evo-r)',
          background: 'var(--evo-fill-warning)',
        }}>
          <span style={{ display: 'inline-flex', flexShrink: 0, color: 'var(--evo-amber)', marginTop: 1 }}>
            <Icon name="flag" size={14} />
          </span>
          <span style={{ flex: 1, fontSize: 'var(--evo-fs-body)', fontFamily: 'var(--evo-font)', lineHeight: 1.6, color: 'var(--evo-fg-2)' }}>
            {avisoPago}
          </span>
        </div>
      )}
      <DocKV label={t('billing.payment.mode')}>
        {editable ? (
          <Dropdown<Invoice['paymentMode']>
            value={invoice.paymentMode}
            size="sm"
            options={[
              { value: 'manual', label: t('billing.payment.modeManual') },
              { value: 'stripe_once', label: t('billing.payment.modeOnce') },
              { value: 'stripe_recurring', label: t('billing.payment.modeRecurring') },
            ]}
            onChange={mode => setMode.mutate({ id: invoice.id, mode, interval: 'month' })}
          />
        ) : (
          t(`billing.payment.mode${invoice.paymentMode === 'stripe_recurring' ? 'Recurring' : invoice.paymentMode === 'stripe_once' ? 'Once' : 'Manual'}`)
        )}
      </DocKV>

      <DocKV label={t('billing.payment.status')}>
        <Pill tone={PAYMENT_STATUS_TONES[invoice.paymentStatus]} size="xs">
          {t(`billing.payment.status_${invoice.paymentStatus}`)}
        </Pill>
      </DocKV>

      {charge && (
        <>
          <DocKV label={t('billing.payment.agreement')}>
            <Pill tone={CHARGE_STATUS_TONES[charge.status]} size="xs">
              {t(`billing.payment.charge_${charge.status}`)}
            </Pill>
          </DocKV>
          <DocKV label={t('billing.payment.amountPerPeriod')}>
            {formatMoney(charge.amountCents, charge.currency)}
            {' · '}
            {t(charge.interval === 'year' ? 'billing.payment.perYear' : 'billing.payment.perMonth')}
          </DocKV>
          <DocKV label={t('billing.payment.nextCharge')}>
            {/* Cancelado o sin tarjeta todavía: no hay próximo cobro, y decir
                una fecha ahí sería mentir. */}
            {charge.currentPeriodEnd ? formatFecha(charge.currentPeriodEnd) : undefined}
          </DocKV>
          <DocKV label={t('billing.payment.card')}>
            {(() => {
              const etiqueta = paymentMethodLabel(charge.cardBrand, charge.cardLast4);
              if (!etiqueta) return undefined;
              return charge.cardStatus === 'failing'
                ? `${etiqueta} — ${t('billing.payment.cardFailing')}`
                : etiqueta;
            })()}
          </DocKV>
        </>
      )}

      {/* Historial: un renglón por intento, los fallidos incluidos. Un cobro
          que rebotó y después salió bien es información, no ruido — es lo que
          explica por qué el cliente escribió preguntando. */}
      {payments.length > 0 && (
        <DocKV label={t('billing.payment.history')} last>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>
            {payments.map(p => (
              <span key={p.id} style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-text-muted)' }}>
                {formatFecha(p.occurredAt)} · {formatMoney(p.amountCents, p.currency)} ·{' '}
                {t(p.status === 'succeeded' ? 'billing.payment.attemptOk' : 'billing.payment.attemptFailed')}
                {p.failureMessage ? ` — ${p.failureMessage}` : ''}
              </span>
            ))}
          </div>
        </DocKV>
      )}
    </Card>
  );
}
